import dynamic from 'next/dynamic';
import React, { useContext } from 'react';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { PROMOTION_TYPES, SearchModes } from '@/domainLogic/index.ts';
import { useTranslation } from '@/app/components/translations/Translation.tsx';
import { getLocationLabel } from '@/app/components/search/components/Card/Details/utils.ts';
import { useCommonClient } from '@/app/common/commonClientProvider.tsx';
import { getCardHref } from '@/app/components/search/utils/buildDealCards/createHotelDetailDeeplink.ts';
import Certification from '@/app/common/components/Certification/index.tsx';
import { useSustainabilityCertificateLabel } from '@/app/common/hooks/useSustainabilityCertificateLabel.ts';
import { Icon } from '@/app/common/icons/Icon';
import type { DealCard } from '../../../utils/buildDealCards/dealCardTypes';
import { CardsContext } from '../../SearchPageContainer/ListContainer/CardsGrid/CardsContext';
import { CARD_TYPE, type HandleDealCardCtaCallBack } from '../types';
import { DetailsContainer, DetailsInnerContainer, HotelName, TopText } from './styled';
import Ratings from './Ratings';
// eslint-disable-next-line import/no-cycle
import Features from './Features';
import Price from './Price';
import { hasPromotion } from '../utils/getPromotionsUtils';
import { ClickEventNameEnum } from '../../../Curiosity/Analytics/utils/ClickEventNameEnum';
import CardDetailsLoader from '../Loaders/Details';
import PriceLoader from '../Loaders/Price';
import { PriceContainer } from './Price/styled';

const AlternativeDates = dynamic(() => import('./AlternativeDates'), { ssr: false });

type DetailsProps = {
  dealCard?: DealCard;
  isOverlay: boolean;
  isNotClickable: boolean;
  promotionType: PROMOTION_TYPES | null;
  handleDealCardCtaCallBack: HandleDealCardCtaCallBack;
  promotionTypes: PROMOTION_TYPES[];
  type?: CARD_TYPE;
};

const Details = ({
  dealCard,
  isOverlay,
  isNotClickable,
  promotionType,
  handleDealCardCtaCallBack,
  promotionTypes,
  type,
}: DetailsProps) => {
  const { isMobile } = useCommonClient();
  const { t } = useTranslation();
  const { localization, searchInfoData, commonData } = usePageWrapperData();
  const { isLoading, isWaiting } = useContext(CardsContext);
  const certificationLabel = useSustainabilityCertificateLabel(dealCard?.product?.accommodation);

  if (!dealCard?.product?.accommodation || (isLoading && !isWaiting)) {
    return (
      <DetailsContainer isOverlay={isOverlay} data-testid="details">
        <DetailsInnerContainer isOverlay={isOverlay}>
          <CardDetailsLoader isOverlay={isOverlay} />
          <Features dealCard={undefined} promotionTypes={promotionTypes} isOverlay={isOverlay} />
        </DetailsInnerContainer>
        <PriceContainer isOverlay={isOverlay} isWaiting={isWaiting} style={{ maxWidth: '100%', minWidth: '0' }}>
          <PriceLoader isOverlay={isOverlay} />
        </PriceContainer>
      </DetailsContainer>
    );
  }

  const { product } = dealCard;
  const { accommodation, dates } = product;
  const soldOut = dates?.soldOut;
  const hotelName = accommodation?.name;
  const soldOutNotLoading = soldOut && !isLoading && !isWaiting;
  const isTsh = hasPromotion(PROMOTION_TYPES.TOP_SECRET_HOTELS, promotionTypes);
  const showAlternativeDates = soldOut && !isOverlay;
  const topText = getLocationLabel(dealCard, localization, t);
  const deepLink = getCardHref({ dealCard, searchInfoData, commonData });
  const isHO = searchInfoData?.searchMode === SearchModes.HO;
  const showFeatures = type !== CARD_TYPE.MODAL;

  return (
    <DetailsContainer
      {...(isNotClickable
        ? {}
        : {
            as: 'a',
            href: deepLink,
            target: '_blank',
            onClick: (e: React.MouseEvent<HTMLDivElement>) =>
              handleDealCardCtaCallBack({ e, payload: { event_name: ClickEventNameEnum.hotelCardClick } }),
          })}
      isOverlay={isOverlay}
      data-testid="details"
    >
      <DetailsInnerContainer soldOut={soldOutNotLoading} isOverlay={isOverlay}>
        <HotelName
          variant={isMobile || isOverlay ? 'Title5' : 'Title4'}
          $isOverlay={isOverlay}
          $isTSH={isTsh}
          id={`card-title-${accommodation.hotelId}`}
        >
          {hotelName}
        </HotelName>
        <Ratings dealCard={dealCard} isOverlay={isOverlay} />
        <Certification label={certificationLabel} isOverlay={isOverlay} variant="card" />
        {!isOverlay && (
          <TopText $isHO={isHO} variant="BodySmall" color="textSecondary">
            <Icon category="Location" name="icon-pin-point" size={14} color="iconSecondary" alt="" />
            {topText}
          </TopText>
        )}
        {showFeatures && <Features dealCard={dealCard} promotionTypes={promotionTypes} isOverlay={isOverlay} />}
        {showAlternativeDates && (
          <AlternativeDates dealCard={dealCard} handleDealCardCtaCallBack={handleDealCardCtaCallBack} />
        )}
      </DetailsInnerContainer>
      {!soldOutNotLoading && (
        <Price
          {...{
            dealCard,
            isOverlay,
            promotionType,
            promotionTypes,
          }}
        />
      )}
    </DetailsContainer>
  );
};

export default Details;
